export interface User {
  id: number;
  name: string;
  email: string;
  balance: number;
  last_login: string;
  is_active: boolean;
  is_admin: boolean;
  reports_count?: number;
  likes_count?: number;
  dislikes_count?: number;
  registration_source?: 'paid_search' | 'organic_search' | 'social' | 'email' | 'referral' | 'direct' | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  referrer_url?: string | null;
  referred_by_user_id?: number | null;
  registration_ip?: string | null;
}

export const SOURCE_BADGE: Record<string, { color: string; label_key: string }> = {
  paid_search:    { color: '#fb923c', label_key: 'sources.paid_search' },
  organic_search: { color: '#22c55e', label_key: 'sources.organic_search' },
  social:         { color: '#3b82f6', label_key: 'sources.social' },
  email:          { color: '#a855f7', label_key: 'sources.email' },
  referral:       { color: '#14b8a6', label_key: 'sources.referral' },
  direct:         { color: '#6b7280', label_key: 'sources.direct' },
};

export interface UsersResponse {
  items: User[];
  total: number;
}

export interface Report {
  id: number;
  user_id: number;
  user_name?: string | null;
  user_email?: string | null;
  name: string;
  created_at: string;
  status: 'completed' | 'processing' | 'error';
  report_type: string;
  is_pro?: boolean;
  file_exists?: boolean;
  file_size?: number | null;
  ticks_used?: number;
  content?: any;
  report_data?: any;
  feedback?: 'like' | 'dislike' | null;
  deleted_at?: string | null;
}

export interface ProviderStats {
  input: number;
  output: number;
  total: number;
  cost_usd: number;
}

export interface UserTokenStats {
  user_id: number;
  user_name?: string;
  user_email?: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  report_count: number;
  cost_usd: number;
  total_paid_rub?: number;
}

export interface DailyTokenStats {
  date: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  report_count: number;
  avg_tokens_per_report: number;
  cost_usd: number;
  providers: Record<string, ProviderStats>;
}

export interface TokenStatsSummary {
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  reports_with_token_data: number;
  reports_without_token_data: number;
  avg_tokens_per_report: number;
  total_cost_usd: number;
  avg_cost_per_report: number;
  total_paid_rub?: number;
  providers: Record<string, ProviderStats>;
}

export interface TokenBreakdownItem {
  name: string;
  input: number;
  output: number;
  total: number;
  provider: string;
  cost_usd?: number;
}

export interface ReportTokenStats {
  report_id: number;
  report_name: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  provider: string;
  has_token_data: boolean;
  cost_usd: number;
  breakdown: {
    analytics: { input: number; output: number; total: number; provider: string; cost_usd?: number };
    reports: TokenBreakdownItem[];
    summary: { input: number; output: number; total: number; provider: string; cost_usd?: number };
  };
}

export interface Stats {
  users: {
    total: number;
    active: number;
    admin: number;
    new_last_30_days: number;
  };
  reports: {
    total: number;
    completed: number;
    processing: number;
    error: number;
    last_30_days: number;
  };
}

export interface TickLog {
  id: number;
  user_id: number;
  amount: number;
  operation_type: string;
  description: string;
  created_at: string;
}

export interface Payment {
  id: number;
  user_id: number;
  user_email: string | null;
  user_name: string | null;
  ticks: number;
  amount: number;
  status: 'pending' | 'success' | 'failed';
  error_message: string | null;
  yoomoney_payment_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PaymentsResponse {
  items: Payment[];
  total: number;
}

export interface PaymentDailyStats {
  date: string;
  payments_count: number;
  total_amount: number;
  total_ticks: number;
}

export interface PaymentMonthlyStats {
  month: string;
  payments_count: number;
  total_amount: number;
  total_ticks: number;
}

export interface PaymentStatsSummary {
  total_payments: number;
  total_amount: number;
  total_ticks: number;
  avg_amount: number;
  avg_ticks: number;
}

export interface PaymentStatsResponse {
  summary: PaymentStatsSummary;
  daily: PaymentDailyStats[];
  monthly: PaymentMonthlyStats[];
}

export interface UserStatsDaily {
  date: string;
  count: number;
}

export interface UserStatsSummary {
  total: number;
  active: number;
  new_last_7_days: number;
  new_last_30_days: number;
  with_reports: number;
  logged_in_last_7_days: number;
  logged_in_last_30_days: number;
}

export interface UserStatsResponse {
  summary: UserStatsSummary;
  registrations_daily: UserStatsDaily[];
  logins_daily: UserStatsDaily[];
  reports_daily: UserStatsDaily[];
}

export const formatAmountRUB = (amount: number): string => {
  return amount.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' \u20BD';
};

export interface PromoCodeItem {
  id: number;
  code: string;
  description: string | null;
  ticks_amount: number;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  is_active: boolean;
  created_by: number;
  creator_email: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface EnvVars {
  [key: string]: string;
}

export interface ActivityLogItem {
  id: number;
  action: string;
  details: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface ActivityLogResponse {
  items: ActivityLogItem[];
  total: number;
}

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  balance: number;
  created_at: string | null;
  last_login: string | null;
  is_active: boolean;
  is_admin: boolean;
  oauth_provider: string | null;
  email_unsubscribed: boolean;
  reports: {
    total: number;
    completed: number;
    errors: number;
    pro_count: number;
    deleted: number;
    ticks_used: number;
    likes: number;
    dislikes: number;
  };
  payments: {
    total: number;
    total_amount: number;
    total_ticks: number;
  };
  tokens: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    cost_usd: number;
    request_count: number;
  };
}

export const formatTokenCount = (count: number): string => {
  if (count >= 1_000_000) return (count / 1_000_000).toFixed(1) + 'M';
  if (count >= 1_000) return (count / 1_000).toFixed(1) + 'K';
  return count.toString();
};

export const formatCostUSD = (cost: number): string => {
  if (!cost || cost === 0) return '$0.00';
  if (cost >= 1) return '$' + cost.toFixed(2);
  if (cost >= 0.01) return '$' + cost.toFixed(3);
  return '$' + cost.toFixed(4);
};

export const getStatusClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'status-completed';
    case 'processing':
      return 'status-processing';
    case 'error':
      return 'status-error';
    default:
      return '';
  }
};

export const getStatusText = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Завершен';
    case 'processing':
      return 'Обрабатывается';
    case 'error':
      return 'Ошибка';
    default:
      return status;
  }
};
